<template>
  <div class="fixed inset-0 z-50 bg-gray-50 pt-safe-area-top">
    <div class="h-full flex flex-col overflow-y-auto">
      <div class="relative h-16 shrink-0 my-4">
        <BackgroundImage
          class="h-full max-w-1/2 mx-auto"
          size="contain"
          :src="groupedQuotes.psp.logo.sm"
          :alt="groupedQuotes.psp.displayName"
        />
        <button class="absolute left-4 top-0 h-full" @click="close">
          <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-sm rotate-90"
             
            alt="back"
            svg-inline
           xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 19"><title>back</title>
  <path fill="currentColor" fill-rule="evenodd"
    d="M.8 1.18a2.26 2.26 0 0 1 3.2 0L16 13.2 28.03 1.18a2.26 2.26 0 0 1 3.2 3.2L17.6 17.97c-.88.9-2.31.9-3.2 0L.8 4.39a2.26 2.26 0 0 1 0-3.2Z"
    clip-rule="evenodd" />
</svg>
        </button>
      </div>

      <div v-if="openPromo" class="flex-1 bg-white rounded-t-16 max-w-lg mx-auto mt-10">
        <div class="-mt-10 flex justify-center rounded-full bg-white w-24 h-24 mx-auto">
          <span class="rounded-full text-blue-900 bg-gray-50 p-4 m-2">
            <svg role="presentation" focusable="false" tabindex="-1" class="icon w-12 h-12"   alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32">
<path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="m2.18 7.6 1.95 9.83 18.75 10.83 7.54-13.05L11.66 4.38 2.18 7.61Zm10.84 6.3a3.3 3.3 0 1 0-5.7-3.3 3.3 3.3 0 0 0 5.7 3.3Z"/>
<path fill="#fff" d="M19.58 19.41a1.82 1.82 0 0 1 1.83-.91 1.79 1.79 0 0 1 1.26.72c.15.2.25.41.3.65.07.23.1.46.06.7a1.79 1.79 0 0 1-.72 1.26 1.85 1.85 0 0 1-2.05.13 1.9 1.9 0 0 1-.56-.5 2.1 2.1 0 0 1-.31-.63 2.1 2.1 0 0 1-.06-.71c.04-.25.12-.48.25-.7Zm2.39 1.38a.87.87 0 0 0 .1-.68.87.87 0 0 0-.43-.54.87.87 0 0 0-.69-.1.87.87 0 0 0-.54.42.87.87 0 0 0-.1.69c.07.24.2.42.43.54.22.13.45.16.68.1.24-.06.42-.2.55-.43Zm-4.73-6.88a1.82 1.82 0 0 1 1.83-.92 1.79 1.79 0 0 1 1.26.73c.15.2.25.4.3.64.08.23.1.47.06.7a1.79 1.79 0 0 1-.72 1.26 1.85 1.85 0 0 1-2.05.13 1.9 1.9 0 0 1-.56-.49 2.1 2.1 0 0 1-.31-.64 2.1 2.1 0 0 1-.06-.71c.04-.24.12-.48.25-.7Zm2.39 1.38a.87.87 0 0 0 .1-.69.87.87 0 0 0-.43-.54.87.87 0 0 0-.69-.1.87.87 0 0 0-.54.43.87.87 0 0 0-.1.68c.07.24.2.42.43.55.22.12.45.15.68.1.24-.07.42-.21.55-.43Zm3.76-.21.52.74-7.27 4.28-.53-.75 7.28-4.27Z"/>
</svg>
          </span>
        </div>
        <PromotionDetails
          :provider-display-name="groupedQuotes.psp.displayName"
          :promotion="openPromo"
        />
      </div>
      <div
        v-else-if="scoreDetailsOpen"
        class="flex-1 bg-white rounded-t-16 max-w-lg mx-auto mt-10 px-4"
      >
        <div class="-mt-10 flex justify-center" v-if="groupedQuotes.psp.score">
          <ScoreCircle class="relative" :score="groupedQuotes.psp.score.value" />
        </div>
        <p class="font-semibold mb-2">
          {{ $t('provider_score', { provider: groupedQuotes.psp.displayName }) }}
        </p>
        <ScoreDetails :score="groupedQuotes.psp.score" :review-link="groupedQuotes.psp.url" />
      </div>
      <MobileProviderServices
        v-else
        :grouped-quotes="groupedQuotes"
        :common-promotion="commonPromotion"
        :mid-market-rate="midMarketRate"
        @open-promo="openPromotion"
        @open-score="openScoreDetails"
      />

      <div
        v-if="isRecommendedProvider(groupedQuotes.psp)"
        class="sticky inset-x-0 bottom-0 bg-white border-t-2 border-gray-200 px-4 text-center pb-safe-area-bottom"
      >
        <ProviderLink
          class="btn-sm w-full max-w-lg my-3"
          :provider="groupedQuotes.psp"
          :promotion="commonPromotion"
          :location="ctaLocation"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { panelOpen } from '../analytics/actions'
import type { GroupedQuotes, Promotion } from '../types'
import { isRecommendedProvider } from '../helpers/recommended'
import ScoreDetails from '../score-details.vue'
import PromotionDetails from '@core/components/provider-promotion/promotion-details.vue'
import MobileProviderServices from './mobile-provider-services.vue'
import ProviderLink from '../provider-link.vue'
import { Comparison } from '../analytics/objects/Comparison'
import { ComparisonSection } from '../analytics/objects/ComparisonSection'
import { ComparisonResult } from '../analytics/objects/ComparisonResult'

const props = defineProps<{
  groupedQuotes: GroupedQuotes
  commonPromotion: Promotion | null
  midMarketRate: number
}>()

const { $track } = useNuxtApp()

const getResultAnalyticsObj = inject('getResultAnalyticsObj') as (
  slug: string,
  location?: string,
) => ComparisonResult | undefined
const sectionAnalyticsObj = inject<Ref<ComparisonSection>>('sectionAnalyticsObj')
const comparisonAnalyticsObj = inject<Ref<Comparison>>('comparisonAnalyticsObj')

const scoreDetailsOpen = ref(false)
const openPromo = ref<Promotion | null>(null)

const ctaLocation = computed(() => {
  if (openPromo.value) return 'promotion_details_screen'
  if (scoreDetailsOpen.value) return 'monito_score_details_panel'
  return 'details_panel'
})

onMounted(() => {
  document.addEventListener('keydown', onEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscape)
})

function onEscape(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    close()
  }
}

const emit = defineEmits(['close'])

function close() {
  if (openPromo.value) {
    openPromo.value = null
  } else if (scoreDetailsOpen.value) {
    scoreDetailsOpen.value = false
  } else {
    emit('close')
  }
}

async function openScoreDetails() {
  scoreDetailsOpen.value = true
  await panelOpen(
    $track,
    'Monito Score Breakdown',
    comparisonAnalyticsObj?.value,
    sectionAnalyticsObj?.value,
    getResultAnalyticsObj(props.groupedQuotes.psp.slug),
  )
}

async function openPromotion(promotion: Promotion) {
  openPromo.value = promotion
  await panelOpen(
    $track,
    'Promotion Details',
    comparisonAnalyticsObj?.value,
    sectionAnalyticsObj?.value,
    getResultAnalyticsObj(props.groupedQuotes.psp.slug),
  )
}
</script>
